import { ChevronRightIcon } from 'lucide-react'
import { useCallback, useMemo, useRef, useState, type KeyboardEvent } from 'react'

import { Input } from '@/components/ui/input'
import { thumbUrl } from '@/features/attachments/api'
import type { FlatNode, TreeNode } from '@/features/trees/api'
import { flatten } from '@/features/trees/api'
import { cn } from '@/lib/utils'

export interface TreeViewProps {
  nodes: TreeNode[]
  selectedId: string | null
  onSelect: (id: string) => void
  /** Label for the count badge, e.g. "parts". Omitted hides the badge. */
  countLabel?: string
  emptyMessage?: string
}

/**
 * The left-hand tree. Filtering matches on the full path, so typing "smd" finds
 * `Passive / Resistors / SMD` and keeps its ancestors visible for context.
 */
export function TreeView({ nodes, selectedId, onSelect, countLabel, emptyMessage }: TreeViewProps) {
  const [filter, setFilter] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  // Roving focus: only the selected item is tabbable, and moving the selection
  // with the keyboard has to move the focus with it or the next key press goes
  // nowhere.
  const itemRefs = useRef(new Map<string, HTMLDivElement>())

  const selectAndFocus = useCallback(
    (id: string) => {
      onSelect(id)
      // The element already exists; focusing it synchronously keeps the key
      // handler attached to whatever the user is looking at.
      itemRefs.current.get(id)?.focus()
    },
    [onSelect],
  )

  const all = useMemo(() => flatten(nodes), [nodes])

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase()

    if (needle) {
      // Keep every match plus the ancestors that give it context.
      const keep = new Set<string>()
      for (const node of all) {
        if (!node.path.toLowerCase().includes(needle)) continue
        keep.add(node.id)
        let parent = node.parentId
        while (parent) {
          keep.add(parent)
          parent = all.find((candidate) => candidate.id === parent)?.parentId ?? null
        }
      }
      return all.filter((node) => keep.has(node.id))
    }

    // Hide anything with a collapsed ancestor.
    return all.filter((node) => {
      let parent = node.parentId
      while (parent) {
        if (collapsed.has(parent)) return false
        parent = all.find((candidate) => candidate.id === parent)?.parentId ?? null
      }
      return true
    })
  }, [all, collapsed, filter])

  function toggle(id: string) {
    setCollapsed((previous) => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>, node: FlatNode, index: number) {
    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault()
        const next = visible[index + 1]
        if (next) selectAndFocus(next.id)
        break
      }
      case 'ArrowUp': {
        event.preventDefault()
        const previous = visible[index - 1]
        if (previous) selectAndFocus(previous.id)
        break
      }
      case 'ArrowRight':
        if (node.hasChildren && collapsed.has(node.id)) {
          event.preventDefault()
          toggle(node.id)
        }
        break
      case 'ArrowLeft':
        if (node.hasChildren && !collapsed.has(node.id)) {
          event.preventDefault()
          toggle(node.id)
        } else if (node.parentId) {
          event.preventDefault()
          selectAndFocus(node.parentId)
        }
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        onSelect(node.id)
        break
      default:
        break
    }
  }

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <Input
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        placeholder="Filter…"
        aria-label="Filter the tree"
        type="search"
      />

      {all.length === 0 ? (
        <p className="text-muted-foreground rounded-lg border border-dashed p-6 text-center text-sm">
          {emptyMessage ?? 'Nothing here yet.'}
        </p>
      ) : visible.length === 0 ? (
        <p className="text-muted-foreground p-3 text-sm">No match for “{filter}”.</p>
      ) : (
        <div role="tree" aria-label="Tree" className="min-h-0 overflow-y-auto">
          {visible.map((node, index) => {
            const isCollapsed = collapsed.has(node.id)
            const selected = node.id === selectedId
            return (
              <div
                key={node.id}
                role="treeitem"
                aria-selected={selected}
                aria-expanded={node.hasChildren ? !isCollapsed : undefined}
                aria-level={node.depth + 1}
                tabIndex={selected ? 0 : -1}
                ref={(element) => {
                  if (element) itemRefs.current.set(node.id, element)
                  else itemRefs.current.delete(node.id)
                }}
                onClick={(event) => {
                  event.currentTarget.focus()
                  onSelect(node.id)
                }}
                onKeyDown={(event) => onKeyDown(event, node, index)}
                style={{ paddingLeft: `${node.depth * 0.9 + 0.25}rem` }}
                className={cn(
                  'flex cursor-pointer items-center gap-1 rounded-md py-1.5 pr-2 text-sm transition-colors',
                  selected ? 'bg-accent text-accent-foreground font-medium' : 'hover:bg-accent/50',
                )}
              >
                {node.hasChildren ? (
                  <button
                    type="button"
                    aria-label={isCollapsed ? `Expand ${node.name}` : `Collapse ${node.name}`}
                    onClick={(event) => {
                      event.stopPropagation()
                      toggle(node.id)
                    }}
                    className="hover:bg-accent grid size-5 shrink-0 place-items-center rounded"
                  >
                    <ChevronRightIcon
                      className={cn('size-3.5 transition-transform', !isCollapsed && 'rotate-90')}
                    />
                  </button>
                ) : (
                  <span className="size-5 shrink-0" />
                )}

                {node.previewAttachmentId && (
                  <img
                    src={thumbUrl(node.previewAttachmentId)}
                    alt=""
                    loading="lazy"
                    className="size-5 shrink-0 rounded-sm border object-cover"
                  />
                )}

                <span className="min-w-0 flex-1 truncate">{node.name}</span>

                {countLabel && node.totalCount > 0 && (
                  <span
                    className="text-muted-foreground tabular shrink-0 text-xs"
                    title={`${node.directCount} directly, ${node.totalCount} including everything beneath`}
                  >
                    {node.totalCount}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
